import React, { useState, useMemo } from 'react';
import styles from './DescriptionRentalTermsTabs.module.css';
import classNames from 'classnames';
import { IconCollection, FAQ } from '../../../components';
import { isHTMLString, normalizeString } from '../../../util/data';

export default function DescriptionRentalTermsTabs({ description, rentalTerms, faqs }) {
    const [activeTab, setActiveTab] = useState('description');
    const [showFullDescription, setShowFullDescription] = useState(false);

    // Helper function to count paragraphs in HTML string
    const countParagraphs = (htmlString) => {
        if(typeof window === 'undefined') return 0;
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlString;
        return tempDiv.querySelectorAll('p').length;
    };

    // Helper function to truncate HTML content
    const truncateHTML = (htmlString, maxLength = 1000) => {
        if (typeof window === 'undefined') return htmlString;
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlString;
        
        let currentLength = 0;
        let truncatedContent = '';
        
        const walkDOM = (node) => {
            if (currentLength >= maxLength) return;
            
            if (node.nodeType === Node.TEXT_NODE) {
                const text = node.textContent;
                const remainingLength = maxLength - currentLength;
                
                if (currentLength + text.length <= maxLength) {
                    truncatedContent += text;
                    currentLength += text.length;
                } else {
                    truncatedContent += text.substring(0, remainingLength);
                    currentLength = maxLength;
                }
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                const tagName = node.tagName.toLowerCase();
                truncatedContent += `<${tagName}`;
                
                // Add attributes
                for (let i = 0; i < node.attributes.length; i++) {
                    const attr = node.attributes[i];
                    truncatedContent += ` ${attr.name}="${attr.value}"`;
                }
                truncatedContent += '>';
                
                // Process child nodes
                for (let i = 0; i < node.childNodes.length; i++) {
                    if (currentLength >= maxLength) break;
                    walkDOM(node.childNodes[i]);
                }
                
                if (currentLength < maxLength) {
                    truncatedContent += `</${tagName}>`;
                }
            }
        };
        
        walkDOM(tempDiv);
        return truncatedContent;
    };

    // Determine if description should be truncated
    const shouldTruncate = useMemo(() => {
        if (!description) return false;
        
        if (isHTMLString(description)) {
            const paragraphCount = countParagraphs(description);
            const charCount = description.replace(/<[^>]*>/g, '').length;
            return charCount > 1000 || paragraphCount > 3;
        } else {
            const charCount = description.length;
            const paragraphCount = (description.match(/\n\s*\n/g) || []).length + 1;
            return charCount > 1000 || paragraphCount > 3;
        }
    }, [description]);

    // Get truncated description
    const truncatedDescription = useMemo(() => {
        if (!shouldTruncate || showFullDescription) return description;
        
        if (isHTMLString(description)) {
            return truncateHTML(description, 1000);
        } else {
            return description.length > 1000 ? description.substring(0, 1000) + '...' : description;
        }
    }, [description, shouldTruncate, showFullDescription]);

    return (
        <section className={styles.panel}>
            <nav className={styles.tabs} aria-label="Rental Information Tabs">
                <button
                    className={`${styles.tab} ${activeTab === 'description' ? styles.active : ''}`}
                    onClick={() => setActiveTab('description')}
                    type="button"
                >
                    Description
                </button>
                <button
                    className={`${styles.tab} ${activeTab === 'rentalTerms' ? styles.active : ''}`}
                    onClick={() => setActiveTab('rentalTerms')}
                    type="button"
                >
                    Rental Terms
                </button>
                {faqs?.length > 0 && (
                    <button
                        className={`${styles.tab} ${styles.mobileOnly} ${activeTab === 'faq' ? styles.active : ''}`}
                        onClick={() => setActiveTab('faq')}
                        type="button"
                    >
                        FAQs
                    </button>
                )}
            </nav>

            <div className={styles.tabContent}>
                {activeTab === 'description' ? (
                    <div className={styles.descriptionContainer}>
                        {isHTMLString(truncatedDescription) ? (
                            <article
                                className={styles.description}
                                dangerouslySetInnerHTML={{ __html: truncatedDescription }}
                            />
                        ) : (
                            <pre className={classNames(styles.preDescription, styles.description)}>{truncatedDescription}</pre>
                        )}
                        {shouldTruncate && (
                            <button
                                className={styles.readMoreButton}
                                onClick={() => setShowFullDescription(!showFullDescription)}
                                type="button"
                            >
                                {showFullDescription ? 'Read Less' : 'Read More'}
                            </button>
                        )}
                    </div>
                ) : activeTab === 'rentalTerms' ? (
                    <section className={styles.terms}>
                        <dl>
                            <div className={styles.termRow}>
                                <dt className={styles.termLabel}>Cash Security Deposit</dt>
                                <dd className={styles.termValue}>{normalizeString(rentalTerms?.cashSecurityDeposit)}</dd>
                            </div>

                            <div className={styles.termRow}>
                                <dt className={styles.termLabel}>Cancelation Policy</dt>
                                <dd className={styles.termValue}>{normalizeString(rentalTerms?.cancelationPolicy)}</dd>
                            </div>

                            <div className={classNames(styles.termRow, styles.termWithItem)}>
                                <dt className={styles.termLabel}>Rental Accessibility</dt>
                                <dd className={styles.termValue}>
                                    {rentalTerms?.rentalAccessibility?.map((item, i) => (
                                        <span className={styles.checkItem} key={i}>
                                            <span className={styles.checkIcon}>
                                                <IconCollection name="advice_check" />
                                            </span>{' '}
                                            {normalizeString(item)}
                                        </span>
                                    ))}
                                </dd>
                            </div>

                            <div className={classNames(styles.termRow, styles.termWithItem)}>
                                <dt className={styles.termLabel}>Trust & Verification Requirements</dt>
                                <dd className={styles.termValue}>
                                    {Array.isArray(rentalTerms?.trustVerification) ? (
                                        rentalTerms.trustVerification.map((item, i) => (
                                            <span className={styles.checkItem} key={i}>
                                                <span className={styles.checkIcon}>
                                                    <IconCollection name="advice_check" />
                                                </span>{' '}
                                                {normalizeString(item)}
                                            </span>
                                        ))
                                    ) : (
                                        rentalTerms?.trustVerification && (
                                            <span className={styles.checkItem}>
                                                <span className={styles.checkIcon}>
                                                    <IconCollection name="advice_check" />
                                                </span>{' '}
                                                {normalizeString(rentalTerms?.trustVerification)}
                                            </span>
                                        )
                                    )}
                                </dd>
                            </div>

                            <div className={classNames(styles.termRow, styles.termWithItem)}>
                                <dt className={styles.termLabel}>Reason For Renting</dt>
                                <dd className={styles.termValue}>
                                    {rentalTerms?.reasonForRenting?.map((item, i) => (
                                        <span className={styles.checkItem} key={i}>
                                            <span className={styles.checkIcon}>
                                                <IconCollection name="advice_check" />
                                            </span>{' '}
                                            {normalizeString(item)}
                                        </span>
                                    ))}
                                </dd>
                            </div>
                        </dl>
                    </section>
                ) : (
                    <section className={styles.faqContent}>
                        {faqs?.length > 0 && <FAQ faq={faqs} />}
                    </section>
                )}
            </div>
        </section>
    );
}
